import { useEffect, type ReactNode } from 'react';

/**
 * The CEO report's drill modal, ported from modal() + openDrill() in
 * ops/report-nightly/pages/ceo.html.
 *
 * Not in ds/ because ds owns the modal's LOOK (.modal, .modal-head,
 * .modal-body, .modal-foot and .scrim are all in ds.css) and this owns its
 * BEHAVIOUR — Escape, scrim click, and locking the page behind it.
 *
 * The close button is styled inline because `.icon-btn` is the one class the
 * published page uses that ds.css does not carry (it reads as a shell selector
 * there, but the modal close is a content control). Reported rather than
 * papered over with a local stylesheet.
 */
export function Modal({ title, count, fixedWindow, foot, onClose, children }: {
  title: string;
  /** Beside the title: how many rows, over what window, on which basis. */
  count: string;
  /** Caption for a figure that cannot follow the page's date picker. */
  fixedWindow?: string;
  foot: ReactNode;
  onClose: () => void;
  children: ReactNode;
}) {
  // The scroll lock belongs to the modal's lifetime, not to the click that
  // opened it: unmounting by any route (Escape, scrim, a re-render upstream)
  // has to give the page its scroll back.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div
      className="scrim"
      // mousedown, not click: a drag that starts inside the modal and ends on
      // the scrim is not a dismissal.
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal">
        <div className="modal-head">
          <span className="modal-title">{title}</span>
          <span className="modal-count">{count}</span>
          {fixedWindow ? <span className="fixed-window">{fixedWindow}</span> : null}
          <span className="spacer" style={{ flex: 1 }} />
          <button
            aria-label="Close"
            onClick={onClose}
            style={{
              width: 34, height: 34, flex: '0 0 34px',
              border: '1px solid rgba(16,24,40,.08)', borderRadius: 10,
              background: 'var(--surface)', color: 'var(--ink-2)',
              display: 'grid', placeItems: 'center',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M3 3l8 8M11 3l-8 8" />
            </svg>
          </button>
        </div>
        <div className="modal-body">{children}</div>
        <div className="modal-foot">{foot}</div>
      </div>
    </div>
  );
}
